import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import Container from "../../components/Container";
import * as Api from "../../api";

// olivv.ai design tokens
const COLORS = {
  primary:    "#00c853",  // accent
  dark:       "#053418",  // forest
  cta:        "#053418",  // forest (used for icon/text on light bg)
  grey:       "#6b7368",  // muted
  line:       "#e6e8df",  // line
  greenGray:  "#e5ead9",  // cream-200
  lighter:    "#d6f1d8",  // accent-soft
  cream:      "#fafaf7",  // cream (page bg)
  cream50:    "#fcfcfa",  // cream-50 (table head)
  ink:        "#1a1a1a",  // ink
  muted2:     "#9aa39a",  // muted-2
  white:      "#ffffff",
};

const MOCK_CHART_DATA = [
  { date: "Jun 1", restaurants: 280, subscriptions: 210 },
  { date: "Jun 3", restaurants: 295, subscriptions: 225 },
  { date: "Jun 5", restaurants: 310, subscriptions: 240 },
  { date: "Jun 7", restaurants: 305, subscriptions: 235 },
  { date: "Jun 9", restaurants: 325, subscriptions: 255 },
  { date: "Jun 11", restaurants: 340, subscriptions: 268 },
  { date: "Jun 13", restaurants: 358, subscriptions: 280 },
  { date: "Jun 15", restaurants: 372, subscriptions: 295 },
  { date: "Jun 17", restaurants: 365, subscriptions: 288 },
  { date: "Jun 18", restaurants: 380, subscriptions: 305 },
];

const UPGRADE_REQUESTS = [
  {
    id: 1,
    restaurant: "The Curry House",
    owner: "Rahul Sharma",
    currentPlan: "Basic",
    requestedPlan: "Pro",
    date: "18 Jun 2026",
    status: "pending",
  },
  {
    id: 2,
    restaurant: "Bistro 21",
    owner: "Emma Watson",
    currentPlan: "Pro",
    requestedPlan: "Enterprise",
    date: "17 Jun 2026",
    status: "pending",
  },
  {
    id: 3,
    restaurant: "Spice Garden",
    owner: "Ali Hassan",
    currentPlan: "Basic",
    requestedPlan: "Pro",
    date: "16 Jun 2026",
    status: "approved",
  },
  {
    id: 4,
    restaurant: "Ocean Bites",
    owner: "Sarah Lee",
    currentPlan: "Basic",
    requestedPlan: "Pro",
    date: "15 Jun 2026",
    status: "rejected",
  },
  {
    id: 5,
    restaurant: "The Green Fork",
    owner: "David Chen",
    currentPlan: "Pro",
    requestedPlan: "Enterprise",
    date: "14 Jun 2026",
    status: "pending",
  },
];

const STATUS_STYLES = {
  pending:  { bg: "#fff8e1", color: "#6b4c00",  label: "Pending" },
  approved: { bg: "#d6f1d8", color: "#053418",  label: "Approved" },
  rejected: { bg: "#fde8e8", color: "#8b1a1a",  label: "Rejected" },
};

const StatCard = ({ icon: Icon, label, value, change, positive }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "12px",
      backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.line}`,
      height: "100%",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <Box>
        <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 13, color: COLORS.grey, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontFamily: "GilroyBold", fontSize: 26, color: COLORS.dark, lineHeight: 1.1 }}>
          {value}
        </Typography>
        {change && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 0.75, gap: 0.4 }}>
            {positive ? (
              <TrendingUpIcon sx={{ fontSize: 14, color: COLORS.primary }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 14, color: "#c0392b" }} />
            )}
            <Typography
              sx={{
                fontFamily: "GilroySemiBold",
                fontSize: 12,
                color: positive ? COLORS.primary : "#c0392b",
              }}
            >
              {change}
            </Typography>
            <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 12, color: COLORS.muted2 }}>
              vs prev 30d
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "10px",
          backgroundColor: COLORS.lighter,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon sx={{ fontSize: 22, color: COLORS.cta }} />
      </Box>
    </Box>
  </Paper>
);

const Dashboard = (props) => {
  const [restaurantCount, setRestaurantCount] = useState("—");
  const [dateRange, setDateRange] = useState("30");

  useEffect(() => {
    Api.restaurantList({}).then((res) => {
      if (res?.success && res?.data) {
        setRestaurantCount(res.data.length ?? res.data.total ?? "—");
      }
    }).catch(() => {});
  }, []);

  const stats = [
    { icon: StorefrontOutlinedIcon, label: "Active Restaurants", value: restaurantCount, change: "+1.2%", positive: true },
    { icon: CardMembershipOutlinedIcon, label: "Active Subscriptions", value: "1,284", change: "+2.8%", positive: true },
    { icon: PeopleOutlinedIcon, label: "Total Customers", value: "18,940", change: "+6.7%", positive: true },
    { icon: PersonAddOutlinedIcon, label: "New Users (30d)", value: "342", change: "+4.5%", positive: true },
    { icon: MonetizationOnOutlinedIcon, label: "Subscription MRR", value: "£38,600", change: "+2.8%", positive: true },
    { icon: RequestQuoteOutlinedIcon, label: "Upgrade Requests", value: "12", change: null, positive: true },
  ];

  const pendingCount = UPGRADE_REQUESTS.filter((r) => r.status === "pending").length;

  return (
    <Container {...props} page="dashboard" onSearch={() => {}}>
      <Box sx={{ maxWidth: 1400 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: "GilroyBold", fontSize: 26, color: COLORS.dark }}>
              Company Overview
            </Typography>
            <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 13.5, color: COLORS.grey, mt: 0.5 }}>
              Everything across Foodo at a glance. Drill into any account, user, or task.
            </Typography>
          </Box>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            size="small"
            sx={{
              fontFamily: "GilroySemiBold",
              fontSize: 13,
              borderRadius: "8px",
              backgroundColor: COLORS.white,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.line },
            }}
          >
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="90">Last 90 days</MenuItem>
          </Select>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {stats.map((s, i) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
              <StatCard {...s} />
            </Grid>
          ))}
        </Grid>

        {/* Chart + Alerts Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "12px",
                backgroundColor: COLORS.white,
                border: `1px solid ${COLORS.line}`,
              }}
            >
              <Typography sx={{ fontFamily: "GilroyBold", fontSize: 15, color: COLORS.dark, mb: 2 }}>
                Restaurants & Subscriptions — Trend
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={MOCK_CHART_DATA} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradRestaurants" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00c853" stopOpacity={0.22} />
                      <stop offset="95%" stopColor="#00c853" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradSubscriptions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#053418" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#053418" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.line} />
                  <XAxis dataKey="date" tick={{ fontFamily: "GilroyRegular", fontSize: 11, fill: COLORS.grey }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontFamily: "GilroyRegular", fontSize: 11, fill: COLORS.grey }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontFamily: "GilroySemiBold", fontSize: 12, borderRadius: 8, border: `1px solid ${COLORS.line}` }} />
                  <Legend wrapperStyle={{ fontFamily: "GilroySemiBold", fontSize: 12 }} />
                  <Area type="monotone" dataKey="restaurants" name="Restaurants" stroke="#00c853" strokeWidth={2} fill="url(#gradRestaurants)" dot={false} />
                  <Area type="monotone" dataKey="subscriptions" name="Subscriptions" stroke="#053418" strokeWidth={2} fill="url(#gradSubscriptions)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "12px",
                backgroundColor: COLORS.white,
                border: `1px solid ${COLORS.line}`,
                height: "100%",
              }}
            >
              <Typography sx={{ fontFamily: "GilroyBold", fontSize: 15, color: COLORS.dark, mb: 2 }}>
                Quick Stats
              </Typography>
              {[
                { label: "Pending upgrade requests", value: pendingCount, accent: "#fff8e1", textColor: "#6b4c00" },
                { label: "Social feature subscribers", value: "87", accent: COLORS.lighter, textColor: COLORS.dark },
                { label: "Restaurants on Enterprise", value: "23", accent: COLORS.lighter, textColor: COLORS.dark },
                { label: "Free trial restaurants", value: "41", accent: COLORS.greenGray, textColor: COLORS.grey },
              ].map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1.5,
                    borderRadius: "8px",
                    backgroundColor: item.accent,
                    mb: 1,
                  }}
                >
                  <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: 12.5, color: item.textColor }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontFamily: "GilroyBold", fontSize: 18, color: item.textColor }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>

        {/* Upgrade Requests Table */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.line}`,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${COLORS.line}`, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontFamily: "GilroyBold", fontSize: 15, color: COLORS.dark }}>
              Upgrade Requests
            </Typography>
            <Chip
              label={`${pendingCount} pending`}
              size="small"
              sx={{
                fontFamily: "GilroySemiBold",
                fontSize: 11,
                backgroundColor: "#fff8e1",
                color: "#6b4c00",
                height: 22,
              }}
            />
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: COLORS.cream50 }}>
                  {["Restaurant", "Owner", "Current Plan", "Requested Plan", "Date", "Status"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontFamily: "GilroySemiBold",
                        fontSize: 12,
                        color: COLORS.grey,
                        borderBottom: `1px solid ${COLORS.line}`,
                        py: 1.5,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {UPGRADE_REQUESTS.map((row) => {
                  const s = STATUS_STYLES[row.status];
                  return (
                    <TableRow
                      key={row.id}
                      sx={{ "&:hover": { backgroundColor: COLORS.cream }, "&:last-child td": { border: 0 } }}
                    >
                      <TableCell sx={{ fontFamily: "GilroySemiBold", fontSize: 13, color: COLORS.dark, borderBottom: `1px solid ${COLORS.line}` }}>
                        {row.restaurant}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "GilroyRegular", fontSize: 13, color: COLORS.grey, borderBottom: `1px solid ${COLORS.line}` }}>
                        {row.owner}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${COLORS.line}` }}>
                        <Chip label={row.currentPlan} size="small" sx={{ fontFamily: "GilroySemiBold", fontSize: 11, backgroundColor: COLORS.greenGray, color: COLORS.grey, height: 22 }} />
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${COLORS.line}` }}>
                        <Chip label={row.requestedPlan} size="small" sx={{ fontFamily: "GilroySemiBold", fontSize: 11, backgroundColor: COLORS.lighter, color: COLORS.dark, height: 22 }} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: "GilroyRegular", fontSize: 13, color: COLORS.grey, borderBottom: `1px solid ${COLORS.line}` }}>
                        {row.date}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${COLORS.line}` }}>
                        <Chip
                          label={s.label}
                          size="small"
                          sx={{ fontFamily: "GilroySemiBold", fontSize: 11, backgroundColor: s.bg, color: s.color, height: 22 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Container>
  );
};

export default Dashboard;
